<app-menu>
  <div class="manager-shell acm-shell min-h-full bg-[var(--manager-bg)] text-[var(--manager-text)]" [attr.data-theme]="themeMode">
    <div class="mx-auto flex h-[calc(100vh-64px)] min-h-[calc(100vh-64px)] w-full max-w-[1840px] flex-col gap-3 px-4 py-4 sm:px-5 lg:px-6">
      <header class="manager-hero overflow-hidden rounded-[8px] border border-[var(--manager-border)] bg-[var(--manager-panel)] shadow-[0_12px_28px_var(--manager-shadow)]">
        <div class="flex flex-col gap-3 p-4 lg:flex-row lg:items-center lg:justify-between">
          <div class="min-w-0">
            <p class="manager-eyebrow">Admin Tools</p>
            <h1 class="manager-title">Assistant Context Files</h1>
            <div class="mt-3 flex flex-wrap gap-2">
              <span class="manager-pill"><i class="fa-solid fa-file-lines"></i>{{ draftCount }} Drafts</span>
              <span class="manager-pill"><i class="fa-solid fa-code-branch"></i>{{ schemaCount }} Schema</span>
              <span class="manager-pill" [ngClass]="reviewCount ? 'manager-pill--warning' : 'manager-pill--success'"><i class="fa-solid fa-user-check"></i>{{ reviewCount }} Review</span>
              <span class="manager-pill" [ngClass]="invalidCount ? 'manager-pill--danger' : 'manager-pill--success'"><i class="fa-solid fa-brackets-curly"></i>{{ invalidCount }} Invalid</span>
            </div>
          </div>

          <div class="flex flex-wrap items-center gap-2">
            <button type="button" class="manager-secondary-button" [routerLink]="['/permission_manager']">
              <i class="fa-solid fa-shield-halved"></i>
              <span>Permissions</span>
            </button>
            <button type="button" class="manager-secondary-button" (click)="loadFiles()" [disabled]="loadingList || loadingFile || saving">
              <i class="fa-solid" [ngClass]="loadingList ? 'fa-circle-notch fa-spin' : 'fa-rotate'"></i>
              <span>Refresh</span>
            </button>
            <button type="button" class="manager-icon-button" (click)="toggleTheme()">
              <i [class]="getThemeIcon()"></i>
              <span>{{ getThemeLabel() }}</span>
            </button>
          </div>
        </div>
      </header>

      <div *ngIf="errorMessage" class="manager-message manager-message--error">
        <i class="fa-solid fa-triangle-exclamation"></i>
        <span>{{ errorMessage }}</span>
      </div>

      <div *ngIf="successMessage" class="manager-message manager-message--success">
        <i class="fa-solid fa-circle-check"></i>
        <span>{{ successMessage }}</span>
      </div>

      <main class="acm-workspace min-h-0 flex-1">
        <aside class="acm-sidebar">
          <div class="acm-toolbar">
            <label class="acm-field acm-field--wide">
              <span>Search</span>
              <input type="search" [(ngModel)]="searchText" placeholder="manifest, route, title">
            </label>
            <div class="grid grid-cols-2 gap-2">
              <label class="acm-field">
                <span>Type</span>
                <select [(ngModel)]="typeFilter">
                  <option value="all">All</option>
                  <option value="draft">Draft</option>
                  <option value="schema">Schema</option>
                </select>
              </label>
              <label class="acm-field">
                <span>State</span>
                <select [(ngModel)]="reviewFilter">
                  <option value="all">All</option>
                  <option value="needs-review">Review</option>
                  <option value="reviewed">Reviewed</option>
                  <option value="invalid">Invalid</option>
                </select>
              </label>
            </div>
          </div>

          <div class="acm-file-list">
            <button
              type="button"
              class="acm-file-row"
              *ngFor="let file of filteredFiles; trackBy: trackByPath"
              [class.is-active]="selectedFile?.path === file.path"
              (click)="selectFile(file)"
            >
              <span class="acm-file-icon" [class.is-danger]="!file.jsonValid">
                <i [class]="fileIcon(file)"></i>
              </span>
              <span class="min-w-0 flex-1">
                <span class="acm-file-title">{{ file.publicTitle || file.manifestKey || file.name }}</span>
                <span class="acm-file-path">{{ file.path }}</span>
              </span>
              <span class="acm-file-badges">
                <span class="acm-chip">{{ fileTypeLabel(file) }}</span>
                <span class="acm-chip" [ngClass]="!file.jsonValid ? 'is-danger' : (file.needsHumanReview ? 'is-warning' : 'is-success')">{{ reviewLabel(file) }}</span>
              </span>
            </button>

            <div *ngIf="!filteredFiles.length && !loadingList" class="manager-empty-state">No files match.</div>
            <div *ngIf="loadingList" class="manager-empty-state"><i class="fa-solid fa-circle-notch fa-spin"></i> Loading files...</div>
          </div>
        </aside>

        <section class="acm-editor-panel">
          <div class="acm-editor-header">
            <div class="min-w-0">
              <p class="manager-eyebrow">{{ selectedFile ? selectedFile.path : 'No File' }}</p>
              <h2 class="manager-section-title">{{ parsedManifest?.publicTitle || parsedManifest?.title || selectedFile?.name || 'Select a file' }}</h2>
              <div class="mt-2 flex flex-wrap gap-2" *ngIf="selectedFile">
                <span class="permission-soft-pill">{{ formatBytes(selectedFile.size) }}</span>
                <span class="permission-soft-pill">{{ editorLineCount }} lines</span>
                <span class="permission-soft-pill">{{ selectedFile.modified | date:'short' }}</span>
                <span class="permission-soft-pill" [ngClass]="jsonError ? 'is-danger' : 'is-success'">{{ jsonError ? 'Invalid JSON' : 'Valid JSON' }}</span>
                <span class="permission-soft-pill" *ngIf="hasChanges">Unsaved</span>
              </div>
            </div>

            <div class="acm-editor-actions">
              <button type="button" class="manager-secondary-button" (click)="formatJson()" [disabled]="!selectedFile || loadingFile || saving">
                <i class="fa-solid fa-wand-magic-sparkles"></i>
                <span>Format</span>
              </button>
              <button type="button" class="manager-secondary-button" (click)="revertFile()" [disabled]="!selectedFile || loadingFile || saving || !hasChanges">
                <i class="fa-solid fa-arrow-rotate-left"></i>
                <span>Revert</span>
              </button>
              <button type="button" class="manager-primary-button" (click)="saveFile()" [disabled]="!canSave">
                <i class="fa-solid" [ngClass]="saving ? 'fa-circle-notch fa-spin' : 'fa-floppy-disk'"></i>
                <span>{{ saving ? 'Saving...' : 'Save' }}</span>
              </button>
            </div>
          </div>

          <div *ngIf="jsonError" class="manager-message manager-message--error">
            <i class="fa-solid fa-code"></i>
            <span>{{ jsonError }}</span>
          </div>

          <div class="acm-editor-shell">
            <textarea
              spellcheck="false"
              [(ngModel)]="editorText"
              (ngModelChange)="onEditorTextChange()"
              [disabled]="!selectedFile || loadingFile || saving"
            ></textarea>
            <div *ngIf="loadingFile" class="acm-editor-overlay">
              <i class="fa-solid fa-circle-notch fa-spin"></i>
              <span>Loading file...</span>
            </div>
          </div>
        </section>

        <aside class="acm-inspector">
          <section class="manager-panel">
            <div class="manager-section-header">
              <div>
                <p class="manager-eyebrow">Manifest</p>
                <h2 class="manager-section-title">Summary</h2>
              </div>
              <span class="acm-chip" *ngIf="selectedFile">{{ fileTypeLabel(selectedFile) }}</span>
            </div>

            <div class="acm-kv">
              <span>Key</span>
              <strong>{{ parsedManifest?.manifestKey || parsedManifest?.title || '-' }}</strong>
            </div>
            <div class="acm-kv">
              <span>Version</span>
              <strong>{{ parsedManifest?.manifestVersion || '-' }}</strong>
            </div>
            <div class="acm-kv">
              <span>Review</span>
              <strong>{{ selectedFile ? reviewLabel(selectedFile) : '-' }}</strong>
            </div>
            <div class="acm-kv">
              <span>Routes</span>
              <strong>{{ selectedRoutePaths.length }}</strong>
            </div>
            <div class="acm-kv">
              <span>Audience</span>
              <strong>{{ selectedAudience.length }}</strong>
            </div>
            <div class="acm-kv">
              <span>Workflows</span>
              <strong>{{ selectedWorkflowCount }}</strong>
            </div>
            <div class="acm-kv">
              <span>Fields</span>
              <strong>{{ selectedFieldCount }}</strong>
            </div>
            <div class="acm-kv">
              <span>Actions</span>
              <strong>{{ selectedActionCount }}</strong>
            </div>
          </section>

          <section class="manager-panel">
            <div class="manager-section-header">
              <div>
                <p class="manager-eyebrow">Routes</p>
                <h2 class="manager-section-title">Paths</h2>
              </div>
            </div>
            <div class="acm-pill-stack" *ngIf="selectedRoutePaths.length; else noRoutes">
              <span class="permission-soft-pill" *ngFor="let route of selectedRoutePaths">{{ route }}</span>
            </div>
            <ng-template #noRoutes>
              <div class="manager-empty-state">No route paths.</div>
            </ng-template>
          </section>

          <section class="manager-panel">
            <div class="manager-section-header">
              <div>
                <p class="manager-eyebrow">Audience</p>
                <h2 class="manager-section-title">Users</h2>
              </div>
            </div>
            <div class="acm-pill-stack" *ngIf="selectedAudience.length; else noAudience">
              <span class="permission-soft-pill" *ngFor="let audience of selectedAudience">{{ audience }}</span>
            </div>
            <ng-template #noAudience>
              <div class="manager-empty-state">No audience entries.</div>
            </ng-template>
          </section>
        </aside>
      </main>
    </div>
  </div>
</app-menu>
